import { useState } from "react";
import { motion } from "framer-motion";
import { Navigation } from "../components/landing/Navigation";
import { Footer } from "../components/landing/Footer";
import { DonateModal } from "../components/landing/DonateModal";
import { storyContent } from "../data/dummy";

const Section = ({
  children,
  className = "",
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    viewport={{ once: true }}
    className={`py-20 px-4 ${className}`}
  >
    <div className="max-w-4xl mx-auto">{children}</div>
  </motion.section>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <motion.h2
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    viewport={{ once: true }}
    className="font-sans font-normal tracking-tight text-3xl md:text-4xl text-foreground mb-10"
  >
    {children}
  </motion.h2>
);

const SubTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="font-sans font-normal tracking-tight text-xl md:text-2xl text-foreground mt-12 mb-4">
    {children}
  </h3>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="text-base leading-relaxed text-foreground mb-4">{children}</p>
);

const ExternalLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-primary hover:text-accent underline-offset-4 hover:underline"
  >
    {children}
  </a>
);

function Interpretation() {
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Navigation onDonateClick={() => setIsDonateModalOpen(true)} />

      {/* Hero */}
      <Section className="bg-white border-b border-border/30">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-sans font-normal tracking-tight text-4xl md:text-5xl text-foreground mb-6">
            The takeaways from single samples.
          </h1>
          <P>
            We tested five popular infant formulas sold in the U.S. This page explains how to read
            the data.{" "}<br />New to the project?{" "}
            <a href="/about" className="text-primary hover:text-accent underline-offset-4 hover:underline">
              Start with the about page →
            </a>
          </P>
        </motion.div>
      </Section>

      {/* The Formulas We Tested */}
      <Section className="bg-muted/20">
        <SectionTitle>The Formulas We Tested</SectionTitle>
        <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4">
          {storyContent.images.map((image, index) => (
            <motion.a
              key={index}
              href={image.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.08, duration: 0.4 }}
              viewport={{ once: true }}
              className="bg-card rounded-xl shadow-card border border-border aspect-square overflow-hidden group flex-shrink-0 w-40"
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </motion.a>
          ))}
        </div>
      </Section>

      {/* Two Sets of Benchmarks */}
      <Section className="bg-white border-t border-border/30">
        <SectionTitle>Two Sets of Benchmarks</SectionTitle>
        <P>The results table has two lenses. Toggle between them in the upper left.</P>
        <P>
          Regulatory limits reflect what's politically and industrially feasible. Health benchmarks
          ask what's likely best for a developing infant. They're not the same.
        </P>
        <div className="space-y-5 mt-2">
          <div className="border-l-2 border-border pl-5">
            <p className="text-base leading-relaxed text-foreground">
              <strong>Regulatory limits</strong> are legal thresholds from the FDA, EU, and
              California's Prop 65. We used the most conservative limit available. Many contaminants
              have no regulatory limit for infant formula.
            </p>
          </div>
          <div className="border-l-2 border-border pl-5">
            <p className="text-base leading-relaxed text-foreground">
              <strong>Health benchmarks</strong> for infants barely exist in the U.S., so we used
              the Environmental Working Group's drinking water limits as a proxy where available. A
              developing infant's brain and organs are entirely reliant on formula. It likely warrants
              as much caution as adult drinking water limits, if not more. Where EWG numbers weren't
              available, we included other benchmarks.
            </p>
          </div>
        </div>
      </Section>

      {/* Two Things to Know */}
      <Section className="bg-muted/20 border-t border-border/30">
        <SectionTitle>Two Things to Know Before You Draw Conclusions</SectionTitle>

        <SubTitle>We ran one sample per formula</SubTitle>
        <P>
          We tested one batch per formula, at a single accredited lab. That's not enough data to
          draw conclusions. Results vary between batches and between testing organizations. Our
          numbers don't always match{" "}
          <ExternalLink href="https://article.images.consumerreports.org/image/upload/v1742584960/prod/content/dam/CRO-Images-2025/Misc/Consumer-Reports-Test-Results-Infant-Formula-v2.pdf">Consumer Reports</ExternalLink>,{" "}
          <ExternalLink href="https://www.momsacrossamerica.com/blog/baby_formula_toxic_metals_results_released">Moms Across America</ExternalLink>,{" "}
          or{" "}
          <ExternalLink href="https://exposingfoodtoxins.com/food-toxins/">Healthy Florida First</ExternalLink>{" "}
          on the same products. Sourcing, manufacturing, and testing methods all introduce variation.
          That's part of why testing should be frequent, independent, and mandatory.
        </P>

        <SubTitle>"&lt; #" means unknown, not clean.</SubTitle>
        <P>
          A "&lt;" number is the smallest amount the lab can reliably identify. Health benchmarks
          are sometimes set below what commercial labs can detect. Results can come back as "not
          detected" even when a contaminant may be present at lower levels that matter.
        </P>
        <P>
          For example, the EWG recommends glyphosate below 5 ppb. The lab detection limit for
          glyphosate is 10.1 ppb, meaning we couldn't confirm whether the formula met that
          recommendation or not.
        </P>
      </Section>

      {/* What We Found */}
      <Section className="bg-white border-t border-border/30">
        <SectionTitle>What We Found</SectionTitle>

        <P>Contaminant levels didn't sort cleanly by origin, certification, or price.</P>
        <P>Three assumptions come up often in parent circles:</P>
        <ol className="list-decimal list-inside space-y-1 mb-4 text-base text-foreground">
          <li>European formula is safer.</li>
          <li>Organic formula is cleaner.</li>
          <li>Pricier formula is less contaminated.</li>
        </ol>
        <P>Our testing didn't support any of that.</P>
        <P>
          The breakdown below shows what the data suggests directionally. You can click any
          contaminant name in the table to sort low to high, and hover over the "i" icons for
          information on the contaminant.
        </P>
        <P>Again, one sample per formula is not enough to draw conclusions.</P>

        <SubTitle>If heavy metals are your primary concern</SubTitle>
        <P>
          Every formula tested positive for arsenic. Bobbie Organic had the lowest total heavy-metal
          sum across arsenic, cadmium, lead, and mercury. Holle Organic had the highest total,
          testing positive for arsenic, cadmium, and lead.
        </P>

        <SubTitle>If phthalates are your primary concern</SubTitle>
        <P>
          All five formulas tested positive for phthalates, the synthetic chemicals used to make
          plastics flexible. Enfamil NeuroPro and Holle Organic tested lowest. Similac tested the
          highest.
        </P>

        <SubTitle>If pesticides are your primary concern</SubTitle>
        <P>
          Enfamil NeuroPro was the only formula with no detected pesticides. Kendamil Organic had
          the highest pesticide reading. DPA showed up in all three organic formulas tested. The
          fact that DPA appeared in all three organic formulas tested is a reminder that organic
          certification doesn't guarantee a contaminant-free product.
        </P>

        <SubTitle>Mycotoxins (mold)</SubTitle>
        <P>No formula tested above detection for any mycotoxin.</P>
      </Section>

      {/* What Needs to Change */}
      <Section className="bg-muted/20 border-t border-border/30">
        <SectionTitle>What Needs to Change</SectionTitle>
        <P>
          We need three big changes to close the gap between legal limits, health benchmarks, and
          the baby formula on the shelf.
        </P>
        <div className="space-y-8">
          <div>
            <SubTitle>Better science.</SubTitle>
            <P>
              The FDA, EWG, EFSA, and WHO often disagree on safe contaminant levels for infants.
              That disagreement lets regulators treat the status quo as acceptable. If no one can
              agree, there is nothing to enforce. Most toxicology data comes from animal studies or
              short-term exposure, not long-term low-dose exposure in infants. We need independent
              research focused specifically on infant safety to build consensus.
            </P>
          </div>
          <div>
            <SubTitle>Updated regulation.</SubTitle>
            <P>
              The formula industry needs real changes in packaging, sourcing, and manufacturing.
              Contaminants are introduced throughout the entire food supply chain, from the raw
              ingredients to the production plant. The goal should be actual safety, not just legal
              compliance.
            </P>
          </div>
          <div>
            <SubTitle>Mandatory testing with enforcement.</SubTitle>
            <P>
              Single-batch, self-reported testing isn't sufficient given how much results vary. We
              need labs that can detect smaller quantities. We need regulators who require frequent,
              independent batch testing. And we need public disclosure of results.
            </P>
          </div>
        </div>
        <div className="mt-10">
          <P>
            To help expand testing or bring this data to policymakers, email{" "}
            <a
              href="mailto:team@fixtheformula.org"
              className="text-primary hover:text-accent underline-offset-4 hover:underline"
            >
              team@fixtheformula.org
            </a>
            . As additional data is collected, we will update the data.
          </P>
        </div>
      </Section>

      <Footer />

      <DonateModal
        isOpen={isDonateModalOpen}
        onClose={() => setIsDonateModalOpen(false)}
      />
    </div>
  );
}

export default Interpretation;
